import React, { useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";

const UploadPage = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState("");
  const [uuid, setUuid] = useState(null);
  const [formValues, setFormValues] = useState({
    uploaderName: "",
    intervieweeName: "",
    email: "",
    publicDescription: "",
    uploadTitle: "",
    notes: "",
    _next: `${window.location.protocol}//${window.location.host}/track-progress/${uuid}`,
  });
  const [error, setError] = useState(false);

  const handleFileChange = (e) => {
    console.log(e.target.files[0]);
    setFile(e.target.files[0]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const uploadFileToGCS = async () => {
    if (!file) return "";

    try {
      setUploading(true);

      console.log("Uploading file:", file);

      // Use FormData to structure the request properly
      const formData = new FormData();
      formData.append("video", file); // Field name should match your backend handler

      const uploadPath =
        file.type === "video/mp4"
          ? `${process.env.REACT_APP_API_URI}/api/upload-video`
          : `https://storage.googleapis.com/upload/storage/v1/b/memento-uploads/o?uploadType=media&name=${encodeURIComponent(
              file.name
            )}`;

      const response = await axios.post(uploadPath, formData, {
        headers: {
          "Content-Type": "multipart/form-data", // Automatically set when using FormData
        },
      });

      // Handle response
      const publicUrl =
        file.type === "video/mp4"
          ? `https://souvenons.com/track-progress/${response.data.uuid}`
          : `https://storage.googleapis.com/memento-uploads/${file.name}`;

      setUploadedFileUrl(publicUrl);
      setUuid(response.data.uuid);

      return { publicUrl, uuid: response.data.uuid };
    } catch (error) {
      console.error("Error uploading file:", error);
      setError(true); // Display Snackbar on upload error
      return "";
    } finally {
      // setUploading(false);
    }
  };

  const addUnindexedVideoToDatabase = async (uuidValue) => {
    // Add the API call to submit transcription data
    try {
      const transcriptionData = {
        id: uuidValue,
        path: `gs://memento-transcriptions/${uuidValue}/final_transcription.txt`,
        submitter_name: formValues.uploaderName,
        submitter_email: formValues.email,
        submitter_phone: null, // Assuming no phone input in the form
        names_of_interviewees: formValues.intervieweeName,
        title: formValues.uploadTitle,
        description: formValues.publicDescription,
        notes: formValues.notes,
      };

      console.log("Submitting transcription data:", transcriptionData);

      await axios.post(
        "https://souvenons-api.conejoplata.workers.dev/api/add_unindexed_transcription",
        transcriptionData
      );

      console.log("Transcription data submitted successfully.");
    } catch (apiError) {
      console.error("Error submitting transcription data:", apiError);
      setError(true);
      return;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("Submitting form...");

    const { uuid: uuidValue, publicUrl } = await uploadFileToGCS();

    if (!uuidValue || !publicUrl) {
      console.error("File upload failed.");
      setError(true); // Trigger the Snackbar
      return;
    }

    await addUnindexedVideoToDatabase(uuidValue);

    const form = document.createElement("form");
    form.action = "https://formsubmit.co/2247fa7b0f1cb81b1a8dcd70acfe0a53";
    form.method = "POST";

    Object.entries({
      ...formValues,
      uploadedFileUrl: publicUrl, // Hidden field for the file URL,
      _next: `${window.location.protocol}//${window.location.host}/track-progress/${uuidValue}`,
    }).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    form.remove();
  };

  return (
    <Container maxWidth="md" style={{ marginTop: "50px" }}>
      <Typography variant="h4" gutterBottom>
        Upload Your Media
      </Typography>
      <Typography
        variant="body1"
        gutterBottom
        style={{
          fontFamily: "Arial, sans-serif",
          color: "gray",
          fontSize: "18px",
          textAlign: "center",
          marginTop: "10px",
        }}
      >
        You can upload video, audio, or text interviews below. All media is
        independently reviewed to maintain quality standards. If your upload is
        accepted, it will be featured on the site within a few days.
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label="Your name"
              variant="outlined"
              name="uploaderName"
              value={formValues.uploaderName}
              onChange={handleInputChange}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Your email"
              variant="outlined"
              name="email"
              value={formValues.email}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Name of people or person interviewed (in order of appearance)"
              variant="outlined"
              name="intervieweeName"
              value={formValues.intervieweeName}
              onChange={handleInputChange}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Pick a title for your upload"
              variant="outlined"
              name="uploadTitle"
              value={formValues.uploadTitle}
              onChange={handleInputChange}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Public description"
              variant="outlined"
              name="publicDescription"
              value={formValues.publicDescription}
              onChange={handleInputChange}
              fullWidth
              required
              multiline
              rows={4}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Additional notes (these will not be published)"
              variant="outlined"
              name="notes"
              value={formValues.notes}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={4}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" component="label">
              Upload File
              <input
                type="file"
                accept=".mp4,.mp3,.txt,.pdf,.doc,.docx"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            <Typography variant="body2" style={{ marginTop: "10px" }}>
              Accepted file types: .mp4, .mp3, .txt, .pdf, .doc, .docx
            </Typography>
            {file && (
              <Typography variant="body2" style={{ marginTop: "10px" }}>
                Selected file: {file.name}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ position: "relative" }}>
              <input
                type="hidden"
                name="_next"
                value={`${window.location.protocol}//${window.location.host}/track-progress/${uuid}`}
              />
              <input
                type="hidden"
                name="_autoresponse"
                value={`Thank you for your submission to Souvenons! If you uploaded an MP4 file, you can track your video upload process at <a target="_blank" href="https://souvenons.com/track-progress/${uuid}">https://souvenons.com/track-progress/${uuid}</a>. Once it has been uploaded, there's still one more step! We still need you to label the people who appear in the video. You will receive an email link when your video is ready.<br /><br />If you uploaded an MP3 or text file, it will be reviewed soon and we will let you know when it has been added to our database.`}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={uploading}
              >
                Submit
              </Button>
              {uploading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </form>
      <Snackbar
        open={error}
        autoHideDuration={6000}
        onClose={() => setError(false)}
      >
        <Alert
          onClose={() => setError(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          File upload failed. Please try again.
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UploadPage;
